import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchMe, type Language } from '@/api/auth';

export type Direction = 'ltr' | 'rtl';

const RTL_LANGUAGES: readonly Language[] = ['he', 'yi'];

export function directionFor(lang: Language): Direction {
  return RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr';
}

/**
 * Resolves the authenticated session's language from /auth/me and
 * mirrors it onto the <html> element so CSS and assistive tech pick
 * up `lang` and `dir` without per-page boilerplate.
 *
 * Hebrew / Yiddish accounts get dir="rtl" automatically. English stays
 * ltr. Super-admin with no customer resolves to the reseller's
 * default_language, else 'en'.
 */
export function useLanguage(): { language: Language; dir: Direction } {
  const { data } = useQuery({
    queryKey: ['me-language'],
    queryFn: () => fetchMe(),
    staleTime: 5 * 60_000,
  });

  const language: Language = data?.language ?? 'en';
  const dir = directionFor(language);

  useEffect(() => {
    const html = document.documentElement;
    if (html.lang !== language) html.lang = language;
    if (html.dir !== dir) html.dir = dir;
  }, [language, dir]);

  return { language, dir };
}
